/**
 * meta/index.js — the meta economy: prize desk, sets, cosmetics, daily login,
 * weekly challenges, leaderboards and the demo store.
 *
 * This module replaces the original stub and keeps its exported surface
 * (`prizeDesk`, `sets`, `dailyLogin`, `cosmetics`, `leaderboards`) intact —
 * `stalls/manager.js` and `main.js` import from here and neither needed a
 * change.
 *
 * **It installs itself.** Everything world-facing is built on `ENGINE_READY`
 * and updated from the `FRAME` event rather than being called from `main.js`,
 * so bringing the meta layer online costs no edit to a file eight other agents
 * are working in.
 *
 * Layout:
 *   state.js             save slices, ISO week / quarter clock, seeded RNG
 *   prize-sets.js        GDD 8.5 sets, escalating piece costs, seasonal gating
 *   cosmetics-catalog.js GDD 3.4 catalog + equip, including skin recolours
 *   cosmetic-models.js   the meshes, registered with avatar/cosmetics.js
 *   daily-login.js       GDD 8.7 30-day streak calendar
 *   challenges.js        GDD 8.2 weekly Star challenges
 *   leaderboards.js      GDD 7.6 weekly + all-time, simulated rivals
 *   store.js             GDD 8.3/12.4 ticket packs, demo only
 *   prize-desk.js        the counter, the barker, the in-world panels
 *   stations.js          boards, challenge/daily panels, ticket kiosk
 *   panel-ui.js          pressable in-world panels + the pointer system
 *   overlay.js           the desktop DOM view of all of the above
 *
 * @module meta
 */

import { EVENTS } from '../constants.js';
import { bus } from '../core/events.js';
import { engine } from '../core/engine.js';
import { options, provide, logCall } from '../core/testmode.js';
import { gooseController } from '../avatar/goose-controller.js';
import { hub } from '../world/hub.js';

import { sets } from './prize-sets.js';
import { cosmetics } from './cosmetics-catalog.js';
import { dailyLogin } from './daily-login.js';
import { challenges, installChallengeTracking } from './challenges.js';
import { leaderboards } from './leaderboards.js';
import { store, installStorePrompt } from './store.js';
import { prizeDesk } from './prize-desk.js';
import { stations } from './stations.js';
import { overlay } from './overlay.js';
import { installPanelPointer, updatePanelPointer, panelCount } from './panel-ui.js';
import { weekKey, quarter } from './state.js';
import './cosmetic-models.js'; // registers the cosmetic builders

export { sets, cosmetics, dailyLogin, challenges, leaderboards, store, prizeDesk, overlay };

let installed = false;

/** Which overlay screen the station the player is standing at should open. */
const SCREEN_FOR_STATION = { store: 'store', daily: 'daily', boards: 'boards' };

/** @returns {string|null} The screen for wherever the player is standing. */
function screenForPosition() {
  if (prizeDesk.isNear()) return 'sets';
  const station = stations.nearestStation();
  return station ? SCREEN_FOR_STATION[station] : null;
}

/** F opens whatever the player is standing at; Escape closes it. */
function installKeyboard() {
  if (typeof window === 'undefined') return;
  window.addEventListener('keydown', (e) => {
    const tag = document.activeElement?.tagName;
    if (tag === 'INPUT' || tag === 'TEXTAREA') return;

    if (e.key === 'Escape' && overlay.isOpen()) {
      overlay.close();
      return;
    }
    if (e.key !== 'f' && e.key !== 'F') return;

    if (overlay.isOpen()) { overlay.close(); return; }
    const screen = screenForPosition();
    if (screen) overlay.open(screen);
  });
}

/** One frame of meta upkeep. */
function update(dt) {
  const position = gooseController.getPosition?.();
  prizeDesk.update(dt, position);
  stations.update(dt, position);
  updatePanelPointer();
}

/**
 * Bring the meta layer online. Idempotent, and safe to call before the scene
 * exists — it simply does nothing until there is one.
 * @returns {boolean} Whether installation happened.
 */
export function install() {
  if (installed) return false;
  // ENGINE_READY is emitted TWICE: once by `engine.init()` with { engine }, and
  // again by `main.js` at the end of boot. The first fires before `hub.build()`
  // and before the goose exists — installing then yields a prize desk with no
  // counter to stand at, leaderboard boards that were never found (so they keep
  // the factory's "1. —" placeholders), and a skin repaint with no goose to
  // paint. Gate on the world actually being there rather than on which emit
  // this is, so the order in main.js can change without breaking us.
  if (!engine.scene || !hub.root) return false;
  installed = true;

  installChallengeTracking();
  installStorePrompt();

  prizeDesk.build(engine.scene);
  stations.build(engine.scene);
  installPanelPointer(engine.camera, engine.renderer?.domElement);
  overlay.init();
  installKeyboard();

  // The goose exists by now, so a skin equipped in a previous session has
  // something to paint. Before this, equip() had nowhere to put it.
  cosmetics.refresh();

  bus.on(EVENTS.FRAME, ({ dt }) => update(dt));

  // Keep every surface honest about the wallet without polling.
  bus.on(EVENTS.WALLET_CHANGE, () => {
    prizeDesk.refresh();
    stations.refresh();
  });

  provide({ meta: metaApi });

  // GDD 8.7 — greet a returning player with the calendar. Suppressed under
  // ?test=1 so the overlay never covers a screenshot a test is about to take.
  if (!options.test && dailyLogin.isDue()) overlay.open('daily');

  return true;
}

/** @returns {boolean} */
export function isInstalled() {
  return installed;
}

// ─────────────────────────────────────────────────────────────────────────────
// The `__cc.meta` automation surface (ARCHITECTURE §4)
// ─────────────────────────────────────────────────────────────────────────────

/** @type {object} */
export const metaApi = {
  installed: () => installed,

  sets: {
    all: () => sets.all(),
    progress: (setId) => sets.progress(setId),
    pieces: (setId) => sets.pieces(setId),
    available: (setId) => sets.available(setId),
    buy: (pieceId) => sets.purchase(pieceId),
    canAfford: (pieceId) => sets.canAfford(pieceId),
    catalog: () => sets.catalog(),
    nextPiece: (setId) => sets.nextPiece(setId),
    /** Buy every remaining piece of a set. @returns {object[]} Each result. */
    complete: (setId) => sets.pieces(setId)
      .filter((p) => !p.owned)
      .map((p) => sets.purchase(p.id)),
  },

  cosmetics: {
    catalog: () => cosmetics.catalog(),
    owned: () => cosmetics.owned(),
    equipped: () => cosmetics.equipped(),
    equip: (slot, id) => cosmetics.equip(slot, id),
    unequip: (slot) => cosmetics.unequip(slot),
    activeSkin: () => cosmetics.activeSkin(),
    slots: () => cosmetics.slots(),
  },

  daily: {
    state: () => dailyLogin.state(),
    calendar: () => dailyLogin.calendar(),
    check: () => dailyLogin.check(),
    claim: () => dailyLogin.claim(),
    isDue: () => dailyLogin.isDue(),
  },
  /** Alias — the brief's entry point for driving the login flow in tests. */
  claimDaily: () => dailyLogin.claim(),

  challenges: {
    list: () => challenges.list(),
    weekKey: () => challenges.weekKey(),
    counters: () => challenges.counters(),
    starsEarned: () => challenges.starsEarned(),
    simulateRound: (round) => challenges.simulateRound(round),
    simulatePrize: (kind) => challenges.simulatePrize(kind),
  },

  leaderboards: {
    board: (stallId, scope) => leaderboards.board(stallId, scope),
    top: (stallId, n, scope) => leaderboards.top(stallId, n, scope),
    submit: (stallId, score) => leaderboards.submit(stallId, score),
    lines: (stallId, scope) => leaderboards.boardLines(stallId, scope),
    weeklyBest: (stallId) => leaderboards.weeklyBest(stallId),
    weekKey: () => leaderboards.weekKey(),
    stalls: () => leaderboards.stalls(),
    /** Which stall the physical hub boards are showing right now. */
    boardStall: () => stations.currentBoardStall(),
    nextBoardStall: () => stations.nextBoardStall(),
    /** What the two physical hub boards actually have painted on them. */
    boardState: () => stations.boardState(),
  },

  store: {
    packs: () => store.packs(),
    buy: (packId) => store.buy(packId),
    isEnabled: () => store.isEnabled(),
    setEnabled: (on) => {
      const value = store.setEnabled(on);
      stations.setStoreVisible(value);
      if (overlay.isOpen()) overlay.open(value ? undefined : 'sets');
      return value;
    },
    /** GDD 12.4 — the kiosk must leave the world, not just refuse to sell. */
    kioskVisible: () => stations.storeVisible(),
    confirmText: (packId) => store.confirmText(packId),
    demoLabel: () => store.demoLabel(),
  },

  desk: {
    open: () => prizeDesk.open(),
    close: () => prizeDesk.close(),
    isOpen: () => prizeDesk.isOpen(),
    isNear: () => prizeDesk.isNear(),
    showSet: (setId) => prizeDesk.showSet(setId),
    openSet: () => prizeDesk.openSet(),
    interactPoint: () => prizeDesk.interactPoint().toArray(),
    purchase: (pieceId) => prizeDesk.purchase(pieceId),
  },

  overlay: {
    open: (screen) => { overlay.open(screen); return overlay.currentScreen(); },
    close: () => { overlay.close(); return true; },
    isOpen: () => overlay.isOpen(),
    screen: () => overlay.currentScreen(),
  },

  /** Panels currently pressable in the world. */
  panels: () => panelCount(),
  /** ISO week driving challenges and the weekly boards. */
  weekKey: () => weekKey(),
  /** Calendar quarter driving seasonal set availability. */
  season: () => quarter(),
};

// ── Self-installation ────────────────────────────────────────────────────────
// main.js imports `dailyLogin` from here, so this module is always evaluated
// during boot. Both ENGINE_READY emissions are handled; `install()` itself
// decides which one is late enough to build against (see its comment).
bus.on(EVENTS.ENGINE_READY, () => {
  try {
    install();
  } catch (err) {
    console.error('[meta] install failed:', err);
    if (Array.isArray(window.__ccErrors)) window.__ccErrors.push(`meta: ${err.message}`);
  }
});

// Expose the surface immediately so a test that reaches `__cc.meta` before
// ENGINE_READY gets the real object rather than null.
provide({ meta: metaApi });
logCall('meta', 'module.loaded', []);

export default {
  prizeDesk, sets, dailyLogin, cosmetics, leaderboards, challenges, store, overlay,
  install, isInstalled, metaApi,
};
